/**
 * DocumentChild[] → markdown converter
 * ────────────────────────────────────
 * The reverse of `markdownToDocumentChildren`: serialises literal contract
 * content (paragraphs, headings, lists, simple tables, inline bold/italic/links)
 * back to markdown so a rich-text editor can be seeded from an existing static
 * section. Deferred blocks (contentBlock / dynamicBlock / signature / toc) are
 * skipped - they are not literal prose and are edited via their own controls.
 *
 * This is a best-effort projection for editing, NOT a loss-less round trip:
 * per-run fonts/sizes/colours and exotic spacing are intentionally dropped (the
 * normalized editor does not express them), mirroring DocContractView.
 */
function isTextRun(run) {
    return !('type' in run);
}
const HEADING_HASHES = {
    Title: '#',
    Heading1: '#',
    Heading2: '##',
    Heading3: '###',
    Heading4: '####',
    Heading5: '#####',
    Heading6: '######',
};
/** Join a paragraph's runs into inline markdown (bold / italic / link marks + <br> breaks). */
function inlineMarkdown(runs) {
    let out = '';
    for (const run of runs) {
        if (!isTextRun(run))
            continue;
        // Honor intra-paragraph line breaks (e.g. a multi-line table-cell like
        // "**Practice Manager**<br><br>Governance:"): `run.break` is the number of <br> that
        // PRECEDE this run - the inverse of inlineRuns' hardBreak->break in markdown-to-children.
        // Without this, the View's children->markdown->children round-trip silently flattens the
        // cell to one run-on line (the export, which renders children directly, is unaffected).
        if (run.break && run.break > 0)
            out += '<br>'.repeat(run.break);
        let text = run.text ?? '';
        if (!text)
            continue;
        if (run.highlight)
            text = `==${text}==`;
        if (run.bold)
            text = `**${text}**`;
        if (run.italics)
            text = `*${text}*`;
        if (run.link)
            text = `[${text}](${run.link})`;
        out += text;
    }
    return out;
}
function paragraphMarkdown(p) {
    const text = inlineMarkdown(p.children);
    if (p.heading)
        return `${HEADING_HASHES[p.heading] ?? '#'} ${text}`.trimEnd();
    if (p.list) {
        const indent = '  '.repeat(Math.max(0, p.list.level ?? 0));
        const marker = p.list.kind === 'ordered' ? '1.' : '-';
        return `${indent}${marker} ${text}`;
    }
    return text;
}
function cellText(cell) {
    return (cell.children
        .filter((c) => c.type === 'paragraph')
        .map((p) => inlineMarkdown(p.children))
        // Separate stacked cell paragraphs with <br> (not a space) so multi-line cells keep
        // their line structure; pipes are escaped so cell text can't break the table grid.
        .join('<br>')
        .replace(/\|/g, '\\|')
        .replace(/^(?:<br>)+|(?:<br>)+$/g, '') // drop stray leading/trailing breaks
        .trim() || ' ');
}
function tableMarkdown(table) {
    const rows = table.rows.map((row) => row.children.map(cellText));
    const header = rows[0];
    if (!header)
        return '';
    const line = (cells) => `| ${cells.join(' | ')} |`;
    const separator = header.map(() => '---');
    return [line(header), line(separator), ...rows.slice(1).map(line)].join('\n');
}
export function documentChildrenToMarkdown(children) {
    const blocks = [];
    let listBuffer = [];
    const flushList = () => {
        if (listBuffer.length) {
            blocks.push(listBuffer.join('\n'));
            listBuffer = [];
        }
    };
    for (const child of children) {
        if (child.type === 'paragraph' && child.list) {
            listBuffer.push(paragraphMarkdown(child));
            continue;
        }
        flushList();
        if (child.type === 'paragraph') {
            blocks.push(paragraphMarkdown(child));
        }
        else if (child.type === 'table') {
            blocks.push(tableMarkdown(child));
        }
        // Deferred blocks (contentBlock/dynamicBlock/signature/toc/pageBreak) are skipped.
    }
    flushList();
    return blocks.join('\n\n').trim();
}
//# sourceMappingURL=children-to-markdown.js.map